import { useState } from 'react'
import { useForm } from 'react-hook-form'
import { zodResolver } from '@hookform/resolvers/zod'
import { loginSchema, type LoginInput } from '@/shared/lib/validations'
import { useAuthStore } from '@/shared/stores/auth'
import { useAuthDialogStore } from '@/features/auth/stores/auth-dialog'
import { apiClient } from '@/shared/lib/api'
import { Button } from '@/shared/components/ui/button'
import { Input } from '@/shared/components/ui/input'
import { Label } from '@/shared/components/ui/label'
import {
  Dialog,
  DialogContent,
  DialogDescription,
  DialogHeader,
  DialogTitle,
} from '@/shared/components/ui/dialog'
import { clearStoredReferralCode } from '../lib/referral'
import type { AuthResponse } from '../types'

export function SignInDialog() {
  const { setToken, setUser } = useAuthStore()
  const { isOpen, close, switchView, onSuccess } = useAuthDialogStore()
  const [isLoading, setIsLoading] = useState(false)
  const [error, setError] = useState<string | null>(null)

  const {
    register,
    handleSubmit,
    formState: { errors },
  } = useForm<LoginInput>({
    resolver: zodResolver(loginSchema),
  })

  const onSubmit = async (data: LoginInput) => {
    setIsLoading(true)
    setError(null)
    try {
      const response = await apiClient<AuthResponse>('/auth/login', {
        method: 'POST',
        skipAuthRefresh: true,
        body: JSON.stringify(data),
      })
      if (response.refreshToken) setToken(response.refreshToken)
      setUser(response.user)
      clearStoredReferralCode()
      close()
      onSuccess?.()
    } catch (err) {
      setError(err instanceof Error ? err.message : 'Failed to sign in')
    } finally {
      setIsLoading(false)
    }
  }

  return (
    <Dialog open={isOpen} onOpenChange={(open) => !open && close()}>
      <DialogContent className="!bottom-0 !left-0 !right-0 !top-auto !translate-x-0 !translate-y-0 rounded-none border-x-0 border-b-0 sm:!left-[50%] sm:!top-[50%] sm:!translate-x-[-50%] sm:!translate-y-[-50%] sm:!bottom-auto sm:!right-auto sm:max-w-md sm:rounded-lg sm:border">
        <DialogHeader className="text-center">
          <DialogTitle>Sign In</DialogTitle>
          <DialogDescription>
            Enter your username or email and password to access your account
          </DialogDescription>
        </DialogHeader>

        <form onSubmit={handleSubmit(onSubmit)} className="mt-4 space-y-4">
          {error && (
            <div className="rounded-md bg-destructive/10 p-3 text-sm text-destructive">{error}</div>
          )}

          <div className="space-y-2">
            <Label htmlFor="signin-email">Username or email</Label>
            <Input
              id="signin-email"
              type="text"
              inputMode="email"
              placeholder="Username or email"
              disabled={isLoading}
              {...register('email')}
            />
            {errors.email && <p className="text-sm text-destructive">{errors.email.message}</p>}
          </div>

          <div className="space-y-2">
            <div className="flex items-center justify-between gap-3">
              <Label htmlFor="signin-password">Password</Label>
              <button
                type="button"
                onClick={() => switchView('forgot-password')}
                className="cursor-pointer border-none bg-transparent p-0 text-sm text-primary hover:underline"
              >
                Forgot password?
              </button>
            </div>
            <Input
              id="signin-password"
              type="password"
              placeholder="Enter your password"
              disabled={isLoading}
              {...register('password')}
            />
            {errors.password && (
              <p className="text-sm text-destructive">{errors.password.message}</p>
            )}
          </div>

          <Button type="submit" variant="gradient" className="w-full" disabled={isLoading}>
            {isLoading ? 'Signing in...' : 'Sign In'}
          </Button>
        </form>

        <div className="mt-4 space-y-2">
          <div className="flex items-center gap-3 text-xs text-muted-foreground">
            <span className="h-px flex-1 bg-border" />
            Or continue with
            <span className="h-px flex-1 bg-border" />
          </div>
          <div className="grid gap-2 sm:grid-cols-2">
            <Button
              variant="outline"
              onClick={() => switchView('lnurl-login')}
              disabled={isLoading}
            >
              Lightning
            </Button>
            <Button
              variant="outline"
              onClick={() => switchView('auth47-login')}
              disabled={isLoading}
            >
              Auth47
            </Button>
          </div>
        </div>

        <p className="mt-4 text-center text-sm text-muted-foreground">
          Don&apos;t have an account?{' '}
          <button
            type="button"
            onClick={() => switchView('signup')}
            className="cursor-pointer border-none bg-transparent p-0 text-primary hover:underline"
          >
            Sign up
          </button>
        </p>
      </DialogContent>
    </Dialog>
  )
}
